import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { _ as _object_without_properties } from "@swc/helpers/_/_object_without_properties";
import { useMemo, useEffect, useCallback } from 'react';
import throttle from 'lodash/throttle';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import { selectors as streamSelectors, actions as streamActions } from '../../Stream';
import { createGetDeviationsByIds } from '../../redux/deviations/selectors';
import { createGetDenormalizedDataEntitiesByIds, getDataEntities } from '../../redux/entities/selectors';
import { galleryFolderSchema, collectionFolderSchema } from '../../redux/normalizr/schemas/folder';
import { createGetUsersByIds } from '../../redux/users/selectors';
import { initializeIfNeeded } from '../../Stream/base/actions';
import { denormalize } from 'normalizr';
export function useStream(streamId, throttleTimeout = 500, initConfig) {
    const dispatch = useDispatch();
    useEffect(()=>{
        if (!initConfig) {
            return;
        }
        dispatch(initializeIfNeeded(streamId, initConfig));
    }, [
        streamId,
        initConfig,
        dispatch
    ]);
    const stream = useSelector((state)=>({
            items: streamSelectors.getItems(state, streamId),
            total: streamSelectors.getTotal(state, streamId),
            hasMore: streamSelectors.hasMore(state, streamId),
            hasLess: streamSelectors.getHasLess(state, streamId),
            isFetching: streamSelectors.isFetching(state, streamId),
            isInitialized: streamSelectors.isInitialized(state, streamId),
            errorMessage: streamSelectors.getErrorMsg(state, streamId)
        }), shallowEqual);
    const fetchNext = useMemo(()=>{
        return throttle(()=>{
            dispatch(streamActions.fetchNext(streamId));
        }, throttleTimeout);
    }, [
        dispatch,
        streamId,
        throttleTimeout
    ]);
    const fetchPrevious = useMemo(()=>{
        return throttle(()=>{
            dispatch(streamActions.fetchPrevious(streamId));
        }, throttleTimeout);
    }, [
        dispatch,
        streamId,
        throttleTimeout
    ]);
    const fetchAll = useMemo(()=>{
        return throttle(()=>{
            dispatch(streamActions.fetchAll(streamId));
        }, throttleTimeout);
    }, [
        dispatch,
        streamId,
        throttleTimeout
    ]);
    const cancelFetch = useCallback(()=>dispatch(streamActions.cancelFetchForStream(streamId)), [
        dispatch,
        streamId
    ]);
    useEffect(()=>{
        return ()=>{
            fetchNext.cancel();
            fetchPrevious.cancel();
            fetchAll.cancel();
        };
    }, [
        fetchNext,
        fetchPrevious,
        fetchAll
    ]);
    return _object_spread_props(_object_spread({}, stream), {
        streamId,
        fetchNext,
        fetchPrevious,
        fetchAll,
        cancelFetch
    });
}
export function useNormalizedStream(streamId, schema, throttleTimeout = 500, initConfig) {
    const _useStream = useStream(streamId, throttleTimeout, initConfig), { items } = _useStream, restStream = _object_without_properties(_useStream, [
        "items"
    ]);
    const entities = useSelector(getDataEntities);
    const denormalizedItems = denormalize(items, schema, entities);
    return _object_spread_props(_object_spread({}, restStream), {
        items: denormalizedItems
    });
}
export function useNormalizedDeviationStream(streamId, throttleTimeout = 500, initConfig) {
    const _useStream = useStream(streamId, throttleTimeout, initConfig), { items } = _useStream, restStream = _object_without_properties(_useStream, [
        "items"
    ]);
    const getDeviationsByIds = useMemo(()=>createGetDeviationsByIds(), []);
    const deviations = useSelector((state)=>getDeviationsByIds(state, items));
    return _object_spread_props(_object_spread({}, restStream), {
        items: deviations
    });
}
export function useNormalizedUserStream(streamId, throttleTimeout = 500, initConfig) {
    const _useStream = useStream(streamId, throttleTimeout, initConfig), { items } = _useStream, restStream = _object_without_properties(_useStream, [
        "items"
    ]);
    const getUsersByIds = useMemo(()=>createGetUsersByIds(), []);
    const users = useSelector((state)=>getUsersByIds(state, items));
    return _object_spread_props(_object_spread({}, restStream), {
        items: users
    });
}
export function useNormalizedFolderStream(streamId, gallectionType) {
    const _useStream = useStream(streamId), { items } = _useStream, restStream = _object_without_properties(_useStream, [
        "items"
    ]);
    const getDenormalizedDataEntitiesByIds = useMemo(()=>createGetDenormalizedDataEntitiesByIds(), []);
    const folders = useSelector((state)=>getDenormalizedDataEntitiesByIds(state, gallectionType === 'gallery' ? galleryFolderSchema : collectionFolderSchema, items));
    return _object_spread_props(_object_spread({}, restStream), {
        items: folders
    });
}
